// src/tabs/calendar/_today-helpers.js — PR #169d (Vue Aujourd'hui)
//
// Helpers purs pour la vue "Aujourd'hui" CalendarPanel.
// Détection des mouvements du jour (check-out + check-in) + rotations
// (même logement, départ + arrivée le même jour) pour mise en avant
// du badge "🧹⚡ Ménage urgent".
//
// Sources de données combinées :
//   - reservations (Beds24 v2 + CSV import) : status IN ('confirmed','pending')
//   - pricing_blocks "direct interne FBZ" : guest_first_name IS NOT NULL
//     (les blocks maintenance/blocage simple sans voyageur sont EXCLUS)
//   - status='cancelled' : exclu strictement
//
// CONVENTION end_date (UNIFIÉE depuis ADR-111 + fix _block-helpers.js exclusive) :
//   - reservation : EXCLUSIVE — end_date = jour de checkout (Beds24 standard PMS).
//     Ex : end_date='2026-05-13' → checkout LE 13/05.
//   - pricing_block : EXCLUSIVE — end_date = jour de checkout (Convention A
//     ADR-111, migration 20260601000001, écriture form _PricingBlockCreateBody
//     `endDate > startDate`). Ex : end_date='2026-05-13' → checkout LE 13/05.
//     Source convention block : _block-helpers.js findBlockForDate
//     (b.end_date > dateISO, exclusive).
//   → normalizeMovementItem n'applique PLUS de shift +1 pour les blocks
//     (le shift legacy compensait le bug findBlockForDate inclusive).
//
// CONVENTION arrival_time :
//   - reservation : champ DB chargé via loadResas (Beds24 v2 + CSV import).
//   - pricing_block : champ inexistant DB → forcé null → rendu "—" côté UI.
//
// Pas de side-effect, pas de fetch : appelables inline OU mémoïsés.
//
// Pattern cohérent _block-helpers.js / _min-stay-helpers.js / _block-visual.js.

/**
 * Normalise un item (résa OU block) en structure unifiée pour la vue Today.
 *
 * @param {object} raw — résa OU pricing_block enrichi
 * @param {'reservation'|'pricing_block'} kind
 * @returns {{
 *   id: string,
 *   kind: 'reservation'|'pricing_block',
 *   logement_id: string,
 *   start_date: string,         // ISO — jour d'arrivée (check-in)
 *   checkout_date: string,      // ISO — jour de check-out NORMALISÉ
 *                               //   résa  : raw.end_date (EXCLUSIVE)
 *                               //   block : raw.end_date (EXCLUSIVE depuis ADR-111)
 *   guest_first_name: string|null,
 *   guest_last_name: string|null,
 *   source: string|null,        // 'airbnb'|'booking'|'direct'|null (block direct interne → 'direct')
 *   arrival_time: string|null,  // HH:MM ou null (résa Beds24) ; toujours null pour block
 *   guest_arrival_time: string|null,   // heure RÉELLE voyageur (formulaire) — ADMIN only (ADR-226) ; null pour block
 *   guest_departure_time: string|null, // heure RÉELLE voyageur (formulaire) — ADMIN only (ADR-226) ; null pour block
 *   status: string|null,
 *   total_amount: number|null,
 *   raw                          // référence brute (debug/extension future)
 * }}
 */
export function normalizeMovementItem(raw, kind) {
  if (kind === "pricing_block") {
    return {
      id: raw.id,
      kind: "pricing_block",
      logement_id: raw.logement_id,
      start_date: raw.start_date,
      // Convention A (ADR-111) : block end_date EXCLUSIVE (jour de check-out)
      // — aligné avec reservation. Pas de shift (le shift +1 legacy compensait
      // le bug findBlockForDate inclusive, désormais corrigé).
      checkout_date: raw.end_date,
      guest_first_name: raw.guest_first_name ?? null,
      guest_last_name: raw.guest_last_name ?? null,
      source: "direct",
      // F-1 : pricing_blocks n'a pas d'arrival_time en DB → null (rendu "—").
      arrival_time: null,
      // Heures voyageur (ADR-226) : colonnes propres à reservations → null pour
      // un block direct → GuestTimesPills ne rend rien.
      guest_arrival_time: null,
      guest_departure_time: null,
      status: null,
      total_amount: raw.total_amount ?? null,
      raw,
    }
  }
  // kind === 'reservation'
  return {
    id: raw.id,
    kind: "reservation",
    logement_id: raw.logement_id,
    start_date: raw.start_date,
    // Convention EXCLUSIVE Beds24 : end_date = jour de checkout (inchangé).
    checkout_date: raw.end_date,
    guest_first_name: raw.guest_first_name ?? null,
    guest_last_name: raw.guest_last_name ?? null,
    source: raw.source ?? null,
    arrival_time: raw.arrival_time ?? null,
    // Heures RÉELLES voyageur (dernière soumission formulaire de bienvenue) —
    // ADMIN only (ADR-226). Rendues via GuestTimesPills (rien si non renseigné).
    guest_arrival_time: raw.guest_arrival_time ?? null,
    guest_departure_time: raw.guest_departure_time ?? null,
    status: raw.status ?? null,
    total_amount: raw.total_amount ?? null,
    raw,
  }
}

/**
 * Combine résas + blocks "direct interne FBZ" en une liste unique de movements
 * normalisés. Applique les filtres d'exclusion (cancelled, blocks maintenance
 * sans voyageur).
 *
 * @param {Array<object>} reservations — résas brutes Supabase (déjà filtrées
 *   par CalendarPanel : filterLogement/filterSource/searchQuery).
 * @param {Array<object>} pricingBlocks — pricing_blocks bruts agrégés
 *   (typiquement via globalBlocksByLogement aplati côté caller).
 * @returns {Array<object>} items normalisés (cf. normalizeMovementItem).
 */
export function buildMovementsFromSources(reservations, pricingBlocks) {
  const movements = []
  for (const r of reservations || []) {
    // F-3 R3 — exclure status='cancelled'.
    if (r.status === "cancelled") continue
    movements.push(normalizeMovementItem(r, "reservation"))
  }
  for (const b of pricingBlocks || []) {
    // Exclure blocks maintenance / blocage simple (sans voyageur).
    // Seuls les blocks "direct interne FBZ" (guest_first_name rempli) sont
    // inclus comme mouvements de la vue Today.
    const firstName = b.guest_first_name
    const hasGuest =
      typeof firstName === "string" && firstName.trim() !== ""
    if (!hasGuest) continue
    movements.push(normalizeMovementItem(b, "pricing_block"))
  }
  return movements
}

/**
 * Filtre les movements pour ne garder QUE ceux ayant un mouvement aujourd'hui :
 * soit check-out (checkout_date === todayISO), soit check-in (start_date === todayISO).
 *
 * Le tri retourné est :
 *   - Check-outs d'abord (groupés)
 *   - Check-ins ensuite (groupés)
 *   - À l'intérieur de chaque groupe : tri par arrival_time ASC (null last),
 *     puis par logement_id pour stabilité.
 *
 * @param {Array<object>} movements — items normalisés
 * @param {string} todayISO — YYYY-MM-DD
 * @returns {{checkouts: Array<object>, checkins: Array<object>}}
 */
export function getTodayMovements(movements, todayISO) {
  const checkouts = []
  const checkins = []
  for (const m of movements || []) {
    if (m.checkout_date === todayISO) checkouts.push(m)
    if (m.start_date === todayISO) checkins.push(m)
  }
  const sortFn = (a, b) => {
    // arrival_time null last, ASC sinon.
    const at = a.arrival_time
    const bt = b.arrival_time
    if (at === null && bt === null) {
      return (a.logement_id || "").localeCompare(b.logement_id || "")
    }
    if (at === null) return 1
    if (bt === null) return -1
    if (at !== bt) return at.localeCompare(bt)
    return (a.logement_id || "").localeCompare(b.logement_id || "")
  }
  checkouts.sort(sortFn)
  checkins.sort(sortFn)
  return { checkouts, checkins }
}

/**
 * Détecte les rotations : pour chaque check-in aujourd'hui, vérifie s'il y a
 * un check-out aujourd'hui sur le MÊME logement_id. Si oui → rotation = ménage
 * urgent (badge "🧹⚡ Ménage urgent").
 *
 * @param {{checkouts: Array<object>, checkins: Array<object>}} movements
 * @returns {{
 *   rotations: Array<{logement_id: string, checkout: object, checkin: object}>,
 *   soloCheckouts: Array<object>,
 *   soloCheckins: Array<object>,
 * }}
 */
export function detectRotations({ checkouts, checkins }) {
  const rotations = []
  const usedCheckoutIds = new Set()
  const usedCheckinIds = new Set()
  // Index checkouts par logement_id pour matching O(N) (1 rotation max
  // par logement : cas exotique 2 check-outs/check-ins même jour même
  // logement = impossible si les data sources sont saines).
  const checkoutByLog = new Map()
  for (const co of checkouts) {
    if (!checkoutByLog.has(co.logement_id)) {
      checkoutByLog.set(co.logement_id, co)
    }
  }
  for (const ci of checkins) {
    const co = checkoutByLog.get(ci.logement_id)
    if (co && !usedCheckoutIds.has(co.id) && !usedCheckinIds.has(ci.id)) {
      rotations.push({ logement_id: ci.logement_id, checkout: co, checkin: ci })
      usedCheckoutIds.add(co.id)
      usedCheckinIds.add(ci.id)
    }
  }
  const soloCheckouts = checkouts.filter((co) => !usedCheckoutIds.has(co.id))
  const soloCheckins = checkins.filter((ci) => !usedCheckinIds.has(ci.id))
  return { rotations, soloCheckouts, soloCheckins }
}

/**
 * Aplatit `blocksByLogement` (Map<logId, Array<block>>) en simple array pour
 * passage à `buildMovementsFromSources`.
 *
 * @param {Map<string, Array<object>>|null|undefined} blocksByLogement
 * @returns {Array<object>}
 */
export function flattenBlocksMap(blocksByLogement) {
  if (!blocksByLogement) return []
  const all = []
  for (const [logId, blocks] of blocksByLogement.entries()) {
    if (Array.isArray(blocks)) {
      for (const b of blocks) all.push({ ...b, logement_id: logId })
    }
  }
  return all
}
